import React, { useState, useEffect } from 'react';
import { Users, Calendar, Clock, CheckCircle, Settings, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalAppointments: 0,
    todayAppointments: 0,
    completedAppointments: 0
  });

  const navigate = useNavigate();
  const { isDoctor, isDispatcher } = useAuth();

  useEffect(() => {
    fetch('/api/patients')
      .then(res => res.json())
      .then(patients => setStats(prev => ({ ...prev, totalPatients: patients.length })));

    fetch('/api/appointments')
      .then(res => res.json())
      .then(appointments => {
        const today = new Date().toISOString().split('T')[0];
        const todayAppts = appointments.filter(apt => apt.date === today);
        const completed = appointments.filter(apt => apt.status === 'completed');
        setStats(prev => ({
          ...prev,
          totalAppointments: appointments.length,
          todayAppointments: todayAppts.length,
          completedAppointments: completed.length
        }));
      });
  }, []);

  const statCards = [
    {
      title: 'Всего пациентов',
      value: stats.totalPatients,
      icon: Users,
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-600',
      text: 'text-blue-700',
      num: 'text-blue-800'
    },
    {
      title: 'Всего записей',
      value: stats.totalAppointments,
      icon: Calendar,
      bg: 'bg-green-50',
      iconBg: 'bg-green-600',
      text: 'text-green-700',
      num: 'text-green-800'
    },
    {
      title: 'Записей сегодня',
      value: stats.todayAppointments,
      icon: Clock,
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-500',
      text: 'text-amber-700',
      num: 'text-amber-800'
    },
    {
      title: 'Завершённых',
      value: stats.completedAppointments,
      icon: CheckCircle,
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-600',
      text: 'text-purple-700',
      num: 'text-purple-800'
    }
  ];

  const quickActions = [
    ...(isDispatcher() ? [
      {
        label: 'Добавить пациента',
        icon: Users,
        color: 'bg-blue-600 hover:bg-blue-700',
        action: () => navigate('/patients')
      },
      {
        label: 'Новая запись',
        icon: Calendar,
        color: 'bg-green-600 hover:bg-green-700',
        action: () => navigate('/appointments')
      }
    ] : []),
    {
      label: 'Открыть календарь',
      icon: Clock,
      color: 'bg-purple-600 hover:bg-purple-700',
      action: () => navigate('/')
    },
    ...(isDoctor() ? [
      {
        label: 'Моё расписание',
        icon: Settings,
        color: 'bg-orange-600 hover:bg-orange-700',
        action: () => navigate('/schedule')
      }
    ] : [])
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Панель управления</h1>
        <p className="text-slate-500 mt-1">Общая информация по системе</p>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className={`${card.bg} rounded-2xl p-5 border border-white shadow-sm`}>
              <div className={`inline-flex items-center justify-center w-12 h-12 ${card.iconBg} rounded-xl mb-3`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <p className={`text-4xl font-bold ${card.num}`}>{card.value}</p>
              <p className={`text-sm font-medium ${card.text} mt-1`}>{card.title}</p>
            </div>
          );
        })}
      </div>

      {/* Быстрые действия */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-5">Быстрые действия</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={action.action}
                className={`${action.color} flex items-center justify-between px-5 py-4 rounded-xl text-white font-semibold text-base transition-colors shadow-sm`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="h-5 w-5" />
                  <span>{action.label}</span>
                </div>
                <ArrowRight className="h-4 w-4 opacity-75" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
